import {
  Box,
  TextField,
  Typography,
  Autocomplete,
  Chip,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import {useEffect, useMemo, useState} from 'react';
import {
  orgUserServiceCreate,
  orgUserServiceUpdate,
  orgUserServiceFind,
  orgUserServiceUpdateCallMethod,
  apartmentService,
} from 'src/apis/autoGenerate/services';
import {OrgUser} from 'src/apis/autoGenerate/types/orgUser';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import Modal from 'src/components/Modal';
import message from 'src/components/Message';
import {UserTypeSelect} from 'src/components/UserTypeSelect';
import {AutocompleteOption} from 'src/components/SearchApartment';
import {useCanEdit, useOrgId, useLicenseQuota} from 'src/hooks';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';

interface AddUserModalProps {
  open: boolean;
  handleConfirm: VoidFunction;
  handleCancel: VoidFunction;
  initData?: OrgUser;
}

function getInitFormData() {
  return {
    name: '',
    email: '',
    roleId: '',
    apartmentIds: [] as string[],
    phone: '',
    countryCode: '+1',
    phoneExt: '',
    callMethod: 'MOBILE_APP' as 'MOBILE_APP' | 'SIP_PHONE',
  };
}

// Phone number validation
function validatePhoneNumber(phone: string): boolean {
  if (!phone) return true; // Optional field
  const cleaned = phone.replace(/\D/g, '');
  // Validates 10-digit phone numbers (US format)
  return cleaned.length === 10;
}

// Phone extension validation
function validatePhoneExtension(ext: string): boolean {
  if (!ext || ext.trim() === '') return true; // Optional field
  // Must contain only digits (0-9) and max 20 characters
  return /^\d{1,20}$/.test(ext.trim());
}

// Format phone number as user types
function formatPhoneNumber(value: string): string {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) {
    return cleaned;
  } else if (cleaned.length <= 6) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  } else {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
}

// Parse phone number from full format (e.g., "+11234567890" -> "1234567890")
function parsePhoneNumber(phone: string): string {
  if (!phone) return '';
  // Remove all non-digit characters first
  const digitsOnly = phone.replace(/\D/g, '');
  // If it starts with 1 and has 11 digits, remove the leading 1 (US country code)
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return digitsOnly.slice(1);
  }
  // If it has 10 digits, return as is
  if (digitsOnly.length === 10) {
    return digitsOnly;
  }
  // Otherwise, try to remove country code pattern (+1, +11, etc.)
  // Remove leading 1 if present (US country code)
  if (digitsOnly.length > 10 && digitsOnly.startsWith('1')) {
    return digitsOnly.slice(1);
  }
  return digitsOnly;
}

// Remove country code from phone number string for display
function removeCountryCodeFromPhone(phone: string): string {
  if (!phone) return '';
  // Remove +1, +1 with spaces, or just 1 at the start
  return phone
    .replace(/^\+\s*1\s*/i, '')
    .replace(/^\+1\s*/, '')
    .replace(/^1\s*/, '')
    .trim();
}

// Extract country code from phone string
function extractCountryCode(phone: string): string {
  if (!phone) return '+1';
  // For US/Canada numbers, always use +1
  // Check if it starts with +1 (must check this BEFORE checking for longer codes)
  // This handles cases like +11234567890 where we want +1, not +112
  if (phone.startsWith('+1')) {
    // Make sure it's not a longer country code by checking the next character
    // If the next character after +1 is a digit, it's part of the phone number, so country code is +1
    // If it's not a digit or the string is just +1, country code is +1
    if (phone.length === 2 || (phone.length > 2 && /^\d/.test(phone.slice(2)))) {
      return '+1';
    }
  }
  // For other countries, match country code (1-3 digits after +)
  const match = phone.match(/^(\+\d{1,3})/);
  return match ? match[1] : '+1';
}

export default function AddUserModal({
  open,
  initData,
  handleConfirm,
  handleCancel,
}: AddUserModalProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(getInitFormData());
  const {orgId} = useOrgId();
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [loadingApartments, setLoadingApartments] = useState(false);
  const [selectedApartments, setSelectedApartments] = useState<Apartment[]>([]);
  const [phoneError, setPhoneError] = useState(false);
  const [phoneExtError, setPhoneExtError] = useState(false);

  // Get current user account info
  const account = useAppSelector(selectAccount);

  // SIP license quota gates the call method control
  const {hasSipLicense, hasSipQuotaAvailable, sipAvailableUnits} = useLicenseQuota();

  // A user already on SIP keeps their seat, so they can always stay on it
  const isAlreadySip = initData?.callMethod === 'SIP_PHONE';
  const canSelectSip = hasSipQuotaAvailable || isAlreadySip;

  // Check if current user is organization owner (memoized to avoid re-renders)
  const canEditOrgUsers = useCanEdit();

  const isOrgOwner = useMemo(() => {
    if (!account.userDetail?.organizations || !orgId) return false;
    const currentOrg = account.userDetail.organizations.find(org => org.id === orgId);
    return String(currentOrg?.ownerId ?? '') === String(account.userDetail?.id ?? '');
  }, [account.userDetail, orgId]);

  const canManagePropertyPermissions = useMemo(
    () => canEditOrgUsers || isOrgOwner,
    [canEditOrgUsers, isOrgOwner]
  );

  // Fetch apartments list on component mount
  useEffect(() => {
    if (orgId && open) {
      setLoadingApartments(true);
      // If user is Org Owner, get all apartments. Otherwise, get only apartments they have access to
      const userId = isOrgOwner ? undefined : account.userId;
      apartmentService
        .apartmentServiceFind(orgId, undefined, undefined, userId)
        .then(res => {
          if (res.data.apartments) {
            setApartments(res.data.apartments);
          }
        })
        .catch(err => {
          console.error('Failed to fetch apartments:', err);
          message.error('Failed to load apartments');
        })
        .finally(() => {
          setLoadingApartments(false);
        });
    }
  }, [orgId, open, isOrgOwner, account.userId]);

  // Load user data and their apartment permissions
  useEffect(() => {
    if (initData) {
      const phone = initData.phone || '';
      const countryCode = extractCountryCode(phone);
      let phoneNumberDigits = parsePhoneNumber(phone);

      // Ensure we have exactly 10 digits (remove any extra digits)
      if (phoneNumberDigits.length > 10) {
        phoneNumberDigits = phoneNumberDigits.slice(-10); // Take last 10 digits
      }

      // Format the phone number for display (ensure it's only 10 digits)
      let phoneNumberFormatted =
        phoneNumberDigits && phoneNumberDigits.length === 10
          ? formatPhoneNumber(phoneNumberDigits)
          : '';

      // Ensure no country code is in the formatted phone number
      phoneNumberFormatted = removeCountryCodeFromPhone(phoneNumberFormatted);

      setFormData({
        name: initData.name as string,
        email: initData.email as string,
        roleId: initData.roleId as string,
        apartmentIds: [],
        phone: phoneNumberFormatted,
        countryCode: countryCode,
        phoneExt: initData.phoneExt || '',
        callMethod: (initData.callMethod as 'MOBILE_APP' | 'SIP_PHONE') || 'MOBILE_APP',
      });

      // Fetch user's current apartment permissions for Org Owner and Org Admin
      // Note: OrgUser.id is the userId we need for apartment permissions
      if (initData.id && orgId && canManagePropertyPermissions) {
        apartmentService
          .apartmentServiceFind(orgId, undefined, undefined, initData.id)
          .then(res => {
            if (res.data.apartments) {
              setSelectedApartments(res.data.apartments);
              setFormData(prev => ({
                ...prev,
                apartmentIds: res.data.apartments?.map(apt => apt.id as string) || [],
              }));
            }
          })
          .catch(err => {
            console.error('Failed to fetch user apartments:', err);
          });
      }
      setPhoneError(false);
      setPhoneExtError(false);
    } else {
      // Reset for new user
      setSelectedApartments([]);
      setFormData(getInitFormData());
      setPhoneError(false);
      setPhoneExtError(false);
    }
  }, [initData, orgId, canManagePropertyPermissions]);

  const checkParams = () => {
    if (!formData.email) {
      message.warning('Email is required');
      return false;
    }
    if (!formData.roleId) {
      message.warning('User Type is required');
      return false;
    }
    // Validate phone number if provided
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      setPhoneError(true);
      message.warning('Please enter a valid 10-digit phone number');
      return false;
    }
    // Validate phone extension if provided
    if (formData.phoneExt && !validatePhoneExtension(formData.phoneExt)) {
      setPhoneExtError(true);
      message.warning('Invalid phone extension format. Extension must contain only digits (0-9).');
      return false;
    }
    setPhoneError(false);
    setPhoneExtError(false);
    return true;
  };

  const handleAddUser = async () => {
    if (!checkParams()) {
      return;
    }
    setLoading(true);

    try {
      // Format phone number with country code
      // Check if phone actually has digits (not just dashes/whitespace)
      // Trim and remove all non-digits to get actual phone digits
      const phoneDigits = formData.phone ? formData.phone.trim().replace(/\D/g, '') : '';
      // Only create full phone if we have at least 10 digits
      const fullPhone =
        phoneDigits && phoneDigits.length >= 10 ? `${formData.countryCode}${phoneDigits}` : null; // Use null to explicitly clear the phone number

      // Format phone extension: trim and use null if empty
      const phoneExt =
        formData.phoneExt && formData.phoneExt.trim() ? formData.phoneExt.trim() : null;

      // Create the user
      await orgUserServiceCreate({
        ...formData,
        orgId,
        phone: fullPhone,
        phoneExt: phoneExt,
      });

      // Grant permissions when properties are selected (owner: any; admin: only from their list)
      if (formData.apartmentIds.length > 0 && canManagePropertyPermissions) {
        // Fetch the newly created user by email to get their ID
        const usersRes = await orgUserServiceFind(formData.email, orgId, 1, undefined);

        if (usersRes.data.users && usersRes.data.users.length > 0) {
          const newUserId = usersRes.data.users[0].id;
          if (newUserId) {
            await apartmentService.apartmentServiceBulkGrantPermissions(newUserId, {
              apartmentIds: formData.apartmentIds,
            });
            message.success('User created and apartment permissions granted successfully');
          } else {
            message.warning('User created but could not grant apartment permissions');
          }
        } else {
          message.warning('User created but could not grant apartment permissions');
        }
      } else {
        message.success('User created successfully');
      }

      handleConfirm();
    } catch (err: any) {
      message.error(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async () => {
    if (!checkParams()) {
      return;
    }
    setLoading(true);

    try {
      // Use orgUserId for the URL (recommended) or fallback to id for backward compatibility
      // According to API docs: orgUserId should be used, but user_id (id) can work with a warning
      const orgUserIdForUrl = initData?.orgUserId || initData?.id;

      if (!orgUserIdForUrl) {
        message.error('Cannot update user: missing user identifier');
        setLoading(false);
        return;
      }

      // Prepare user data without email (email is ignored by the API for updates)
      // and without apartmentIds (handled separately via apartment permissions API)
      // Also exclude phone, phoneExt and countryCode as we handle phone separately
      const {email, apartmentIds, countryCode, phone, phoneExt, callMethod, ...userUpdateData} =
        formData;

      // Format phone number with country code
      // Check if phone actually has digits (not just dashes/whitespace)
      // Trim and remove all non-digits to get actual phone digits
      const phoneDigits = phone ? phone.trim().replace(/\D/g, '') : '';
      // Only create full phone if we have at least 10 digits
      const fullPhone =
        phoneDigits && phoneDigits.length >= 10 ? `${formData.countryCode}${phoneDigits}` : ''; // Use empty string to explicitly clear the phone number

      // Format phone extension: trim and use null if empty
      const formattedPhoneExt =
        formData.phoneExt && formData.phoneExt.trim() ? formData.phoneExt.trim() : null;

      // Update user information
      await orgUserServiceUpdate(orgUserIdForUrl as string, {
        id: orgUserIdForUrl as string,
        user: {
          ...userUpdateData,
          orgId,
          phone: fullPhone,
          phoneExt: formattedPhoneExt,
        },
      });

      // Update call method separately if it changed
      const originalCallMethod =
        (initData?.callMethod as 'MOBILE_APP' | 'SIP_PHONE') || 'MOBILE_APP';
      const newCallMethod = (formData.callMethod || originalCallMethod) as
        | 'MOBILE_APP'
        | 'SIP_PHONE';

      if (initData?.orgUserId && newCallMethod !== originalCallMethod) {
        await orgUserServiceUpdateCallMethod(initData.orgUserId, {
          callMethod: newCallMethod,
        });
      }

      // Update apartment permissions (replace all) for Org Owner and Org Admin
      // Note: OrgUser.id is the userId we need for apartment permissions
      if (initData?.id && canManagePropertyPermissions) {
        await apartmentService.apartmentServiceBulkUpdatePermissions(initData.id, {
          apartmentIds: formData.apartmentIds,
        });
        message.success('User and apartment permissions updated successfully');
      } else {
        message.success('User updated successfully');
      }

      handleConfirm();
    } catch (err: any) {
      message.error(err.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const isEdit = useMemo(() => {
    return !!initData;
  }, [initData]);

  return (
    <Modal
      open={open}
      title={isEdit ? 'Edit User' : 'Add User'}
      handleCancel={handleCancel}
      handleConfirm={isEdit ? handleEditUser : handleAddUser}
      loading={loading}
      modalContent={
        <Box component="form">
          {/* User Name */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            User Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="User Name"
            variant="outlined"
            value={formData.name}
            required
            onChange={(e: any) => {
              setFormData({...formData, name: e.target.value});
            }}
            sx={{mb: 3}}
          />

          {/* Email */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Email <span style={{color: 'inherit'}}>*</span>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Email"
            variant="outlined"
            required
            value={formData.email}
            disabled={isEdit}
            onChange={(e: any) => {
              setFormData({...formData, email: e.target.value});
            }}
            sx={{mb: 3}}
          />

          {/* Phone */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Phone
          </Typography>
          <Box sx={{display: 'flex', gap: 1, mb: 3}}>
            <FormControl size="small" sx={{minWidth: 120}}>
              <InputLabel id="country-code-label">Code</InputLabel>
              <Select
                labelId="country-code-label"
                label="Code"
                value={formData.countryCode}
                disabled
                sx={{
                  '& .MuiSelect-select': {
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <MenuItem value="+1">+1 (US/CA)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              placeholder="123-456-7890"
              variant="outlined"
              value={removeCountryCodeFromPhone(formData.phone)} // Ensure no country code in value
              error={phoneError}
              helperText={phoneError ? 'Please enter a valid 10-digit phone number' : ''}
              onChange={(e: any) => {
                const input = e.target.value;
                // If input is empty, clear the phone field
                if (!input || input.trim() === '') {
                  setFormData({...formData, phone: ''});
                  setPhoneError(false);
                  return;
                }
                // Remove country code if user accidentally types it (e.g., +1, +11, etc.)
                let cleaned = removeCountryCodeFromPhone(input);
                // Only allow digits and dashes, limit to 12 characters (for formatted: 123-456-7890)
                cleaned = cleaned.replace(/[^\d-]/g, '').slice(0, 12);
                const formatted = formatPhoneNumber(cleaned);
                setFormData({...formData, phone: formatted});
                setPhoneError(false);
              }}
              onBlur={() => {
                if (formData.phone && !validatePhoneNumber(formData.phone)) {
                  setPhoneError(true);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{formData.countryCode}</InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Phone Extension */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Extension (Optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="1234"
            variant="outlined"
            value={formData.phoneExt || ''}
            error={phoneExtError}
            helperText={
              phoneExtError
                ? 'Invalid phone extension format. Extension must contain only digits (0-9).'
                : 'Enter phone extension for SIP calls (digits only, max 20 characters)'
            }
            onChange={(e: any) => {
              const input = e.target.value;
              // Only allow digits, limit to 20 characters
              const cleaned = input.replace(/\D/g, '').slice(0, 20);
              setFormData({...formData, phoneExt: cleaned});
              setPhoneExtError(false);
            }}
            onBlur={() => {
              if (formData.phoneExt && !validatePhoneExtension(formData.phoneExt)) {
                setPhoneExtError(true);
              }
            }}
            sx={{mb: 3}}
          />

          {/* Call Method (Edit only, requires a SIP license) */}
          {isEdit && hasSipLicense && (
            <>
              <Box sx={{display: 'flex', alignItems: 'center', mb: 1, gap: 0.5}}>
                <Typography variant="h6" sx={{fontWeight: 400, fontSize: '1.1rem'}}>
                  Call Method
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{maxWidth: 260}}>
                      <Typography variant="body2" sx={{fontWeight: 600, mb: 0.25}}>
                        Mobile App (Default)
                      </Typography>
                      <Typography variant="body2" sx={{mb: 1}}>
                        Incoming calls ring your mobile app.
                      </Typography>
                      <Typography variant="body2" sx={{fontWeight: 600, mb: 0.25}}>
                        SIP Phone
                      </Typography>
                      <Typography variant="body2">
                        Incoming calls ring your phone number instead of the mobile app.
                      </Typography>
                    </Box>
                  }
                  placement="right"
                  arrow
                >
                  <InfoIcon
                    sx={{
                      fontSize: '1rem',
                      color: '#9CA3AF',
                      cursor: 'default',
                    }}
                  />
                </Tooltip>
              </Box>
              <FormControl size="small" fullWidth sx={{mb: 3}}>
                <InputLabel id="call-method-label">Call Method</InputLabel>
                <Select
                  labelId="call-method-label"
                  label="Call Method"
                  value={formData.callMethod || 'MOBILE_APP'}
                  onChange={(e: any) => {
                    const value = e.target.value as 'MOBILE_APP' | 'SIP_PHONE';
                    setFormData({...formData, callMethod: value});
                  }}
                >
                  <MenuItem value="MOBILE_APP">Mobile App</MenuItem>
                  <MenuItem value="SIP_PHONE" disabled={!canSelectSip}>
                    SIP Phone
                  </MenuItem>
                </Select>
                {!canSelectSip && (
                  <FormHelperText>
                    All SIP licenses are in use ({sipAvailableUnits} available). Add SIP
                    licenses on the License page or switch another user to Mobile App.
                  </FormHelperText>
                )}
              </FormControl>
            </>
          )}

          {/* User Type */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            User Type <span style={{color: 'inherit'}}>*</span>
          </Typography>
          <UserTypeSelect
            initVal={initData ? initData.roleId : undefined}
            onChange={(value: AutocompleteOption) => {
              setFormData({...formData, roleId: value.value});
            }}
          />

          {/* Property permissions: owner sees all properties; admin only properties they can access */}
          {canManagePropertyPermissions && (
            <>
              <Typography variant="h6" sx={{mt: 3, mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
                Property Permissions
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                Select properties this user can access (optional)
              </Typography>
              <Autocomplete
                multiple
                fullWidth
                size="small"
                loading={loadingApartments}
                options={apartments}
                value={selectedApartments}
                getOptionLabel={option => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(event, newValue) => {
                  setSelectedApartments(newValue);
                  setFormData({
                    ...formData,
                    apartmentIds: newValue.map(apt => apt.id as string),
                  });
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Select properties"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingApartments ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option.name}
                      {...getTagProps({index})}
                      size="small"
                      key={option.id}
                    />
                  ))
                }
              />
            </>
          )}
        </Box>
      }
    />
  );
}
